/** Shared General work job types for Operator Assign → Requests. */
import type { OperatorDeskWorkItem, OperatorDeskWorkStatus } from './operatorDeskWork';
import { OPERATOR_DESK_STATUS_COLORS, OPERATOR_DESK_STATUS_LABELS } from './operatorDeskWork';

/**
 * Job type groups, in picker order (Shaun 2026-10-08: many more oilfield-specific types).
 * Water delivery, WM, Porta, Welding, Hotshot and Semi have their own Assign feeds, so they are not listed here.
 */
export const OPERATOR_GENERAL_WORK_JOB_TYPE_GROUPS = [
  'Production & lease ops',
  'Well servicing & workover',
  'Completions & frac',
  'Artificial lift',
  'Fluids & hauling',
  'Facilities & pipeline',
  'Civil & site work',
  'Environmental & safety',
  'Plug & abandon',
  'Rentals & equipment',
  'Other',
] as const;

export type OperatorGeneralWorkJobTypeGroup = (typeof OPERATOR_GENERAL_WORK_JOB_TYPE_GROUPS)[number];

/**
 * Stored on operator_desk_general_jobs.job_type. Never rename an id: stored rows and Contractor Desk
 * (`generalJobTypeLabel` humanizes the slug) read it. Labels can change freely.
 * Original ten ids: flowback, completion, workover, rig_move, frac_support, wireline, plug_abandon,
 * construction, maintenance, other.
 */
export const OPERATOR_GENERAL_WORK_JOB_TYPES = [
  // Production & lease ops
  { id: 'pumper', label: 'Pumper / lease operator', group: 'Production & lease ops' },
  { id: 'well_check', label: 'Well check / gauging', group: 'Production & lease ops' },
  { id: 'roustabout', label: 'Roustabout crew', group: 'Production & lease ops' },
  { id: 'lease_maintenance', label: 'Lease maintenance', group: 'Production & lease ops' },
  { id: 'maintenance', label: 'Maintenance (general)', group: 'Production & lease ops' },
  { id: 'chemical_treating', label: 'Chemical treating / injection', group: 'Production & lease ops' },
  { id: 'hot_oiling', label: 'Hot oiling', group: 'Production & lease ops' },
  { id: 'hot_watering', label: 'Hot watering', group: 'Production & lease ops' },
  { id: 'paraffin_cutting', label: 'Paraffin cutting / scraping', group: 'Production & lease ops' },
  { id: 'automation_scada', label: 'Automation / SCADA tech', group: 'Production & lease ops' },
  { id: 'measurement', label: 'Measurement / meter proving', group: 'Production & lease ops' },
  { id: 'lact_service', label: 'LACT unit service', group: 'Production & lease ops' },
  // Well servicing & workover
  { id: 'workover', label: 'Workover rig', group: 'Well servicing & workover' },
  { id: 'pulling_unit', label: 'Pulling unit / rod & tubing job', group: 'Well servicing & workover' },
  { id: 'swabbing', label: 'Swabbing', group: 'Well servicing & workover' },
  { id: 'coiled_tubing', label: 'Coiled tubing', group: 'Well servicing & workover' },
  { id: 'nitrogen_services', label: 'Nitrogen services', group: 'Well servicing & workover' },
  { id: 'slickline', label: 'Slickline', group: 'Well servicing & workover' },
  { id: 'wireline', label: 'Wireline / e-line', group: 'Well servicing & workover' },
  { id: 'logging', label: 'Logging', group: 'Well servicing & workover' },
  { id: 'acidizing', label: 'Acidizing', group: 'Well servicing & workover' },
  { id: 'cementing', label: 'Cementing / squeeze', group: 'Well servicing & workover' },
  { id: 'fishing', label: 'Fishing tools', group: 'Well servicing & workover' },
  { id: 'snubbing', label: 'Snubbing', group: 'Well servicing & workover' },
  { id: 'well_testing', label: 'Well testing', group: 'Well servicing & workover' },
  // Completions & frac
  { id: 'completion', label: 'Completion', group: 'Completions & frac' },
  { id: 'frac_support', label: 'Frac support', group: 'Completions & frac' },
  { id: 'flowback', label: 'Flowback', group: 'Completions & frac' },
  { id: 'sand_hauling', label: 'Sand / proppant hauling', group: 'Completions & frac' },
  { id: 'frac_water_transfer', label: 'Frac water transfer', group: 'Completions & frac' },
  { id: 'rig_move', label: 'Rig move assist', group: 'Completions & frac' },
  { id: 'rig_up_rig_down', label: 'Rig-up / rig-down', group: 'Completions & frac' },
  // Artificial lift
  { id: 'pumping_unit_service', label: 'Pumping unit install / repair', group: 'Artificial lift' },
  { id: 'rod_pump_repair', label: 'Rod pump repair', group: 'Artificial lift' },
  { id: 'esp_service', label: 'ESP service', group: 'Artificial lift' },
  { id: 'gas_lift', label: 'Gas lift', group: 'Artificial lift' },
  { id: 'plunger_lift', label: 'Plunger lift', group: 'Artificial lift' },
  // Fluids & hauling
  { id: 'produced_water_hauling', label: 'Produced water hauling', group: 'Fluids & hauling' },
  { id: 'crude_hauling', label: 'Crude hauling', group: 'Fluids & hauling' },
  { id: 'vacuum_truck', label: 'Vacuum truck', group: 'Fluids & hauling' },
  { id: 'kill_truck', label: 'Transport / kill truck', group: 'Fluids & hauling' },
  { id: 'frac_tank_rental', label: 'Frac tank rental & delivery', group: 'Fluids & hauling' },
  { id: 'swd_disposal', label: 'SWD / disposal', group: 'Fluids & hauling' },
  // Facilities & pipeline
  { id: 'construction', label: 'Construction (general)', group: 'Facilities & pipeline' },
  { id: 'tank_battery', label: 'Tank battery build / repair', group: 'Facilities & pipeline' },
  { id: 'tank_cleaning', label: 'Tank cleaning', group: 'Facilities & pipeline' },
  { id: 'separator_service', label: 'Separator / heater treater service', group: 'Facilities & pipeline' },
  { id: 'compressor_service', label: 'Compressor service', group: 'Facilities & pipeline' },
  { id: 'pipeline_construction', label: 'Pipeline construction', group: 'Facilities & pipeline' },
  { id: 'pipeline_repair', label: 'Pipeline repair / leak', group: 'Facilities & pipeline' },
  { id: 'pigging', label: 'Pigging', group: 'Facilities & pipeline' },
  { id: 'hydrotesting', label: 'Hydrotesting', group: 'Facilities & pipeline' },
  { id: 'cathodic_protection', label: 'Cathodic protection', group: 'Facilities & pipeline' },
  { id: 'flowline_service', label: 'Flowline install / repair', group: 'Facilities & pipeline' },
  { id: 'valve_wellhead_service', label: 'Valve & wellhead service', group: 'Facilities & pipeline' },
  { id: 'fabrication', label: 'Fabrication / skid build', group: 'Facilities & pipeline' },
  { id: 'electrical', label: 'Electrical', group: 'Facilities & pipeline' },
  { id: 'instrumentation', label: 'Instrumentation / I&E', group: 'Facilities & pipeline' },
  // Civil & site work
  { id: 'lease_road_maintenance', label: 'Lease road maintenance', group: 'Civil & site work' },
  { id: 'pad_construction', label: 'Location / pad construction', group: 'Civil & site work' },
  { id: 'dirt_work', label: 'Dirt work / dozer', group: 'Civil & site work' },
  { id: 'caliche_hauling', label: 'Caliche hauling', group: 'Civil & site work' },
  { id: 'hydro_excavation', label: 'Hydro-excavation', group: 'Civil & site work' },
  { id: 'backhoe', label: 'Backhoe / excavator', group: 'Civil & site work' },
  { id: 'mowing_weed_control', label: 'Mowing / weed control', group: 'Civil & site work' },
  { id: 'fencing', label: 'Fencing / cattle guard', group: 'Civil & site work' },
  // Environmental & safety
  { id: 'spill_response', label: 'Spill response / remediation', group: 'Environmental & safety' },
  { id: 'norm_survey', label: 'NORM survey', group: 'Environmental & safety' },
  { id: 'h2s_safety', label: 'H2S monitoring / safety', group: 'Environmental & safety' },
  { id: 'leak_survey', label: 'Gas detection / leak survey (OGI)', group: 'Environmental & safety' },
  { id: 'site_reclamation', label: 'Site reclamation', group: 'Environmental & safety' },
  { id: 'soil_sampling', label: 'Soil sampling', group: 'Environmental & safety' },
  // Plug & abandon
  { id: 'plug_abandon', label: 'Plug & abandon (P&A)', group: 'Plug & abandon' },
  { id: 'surface_restoration', label: 'Surface restoration', group: 'Plug & abandon' },
  { id: 'cut_and_cap', label: 'Wellhead cut & cap', group: 'Plug & abandon' },
  // Rentals & equipment
  { id: 'crane', label: 'Crane', group: 'Rentals & equipment' },
  { id: 'forklift', label: 'Forklift / telehandler', group: 'Rentals & equipment' },
  { id: 'light_tower', label: 'Light tower', group: 'Rentals & equipment' },
  { id: 'generator', label: 'Generator', group: 'Rentals & equipment' },
  { id: 'man_camp', label: 'Housing / man camp', group: 'Rentals & equipment' },
  // Other (free text in job_type_other)
  { id: 'other', label: 'Other', group: 'Other' },
] as const satisfies ReadonlyArray<{ id: string; label: string; group: OperatorGeneralWorkJobTypeGroup }>;

export type OperatorGeneralWorkJobType = (typeof OPERATOR_GENERAL_WORK_JOB_TYPES)[number];

export type OperatorGeneralWorkJobTypeId = OperatorGeneralWorkJobType['id'];

/** Every valid job_type id (server validation + GET jobTypes). */
export const OPERATOR_GENERAL_WORK_JOB_TYPE_IDS: readonly OperatorGeneralWorkJobTypeId[] =
  OPERATOR_GENERAL_WORK_JOB_TYPES.map((t) => t.id);

const JOB_TYPE_BY_ID = new Map<string, OperatorGeneralWorkJobType>(
  OPERATOR_GENERAL_WORK_JOB_TYPES.map((t) => [t.id, t]),
);

/** "Hot Oiling" / "hot-oiling" / "hot_oiling" → 'hot_oiling'; unknown → null. */
export function normalizeOperatorGeneralWorkJobType(raw: unknown): OperatorGeneralWorkJobTypeId | null {
  const key = String(raw ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return JOB_TYPE_BY_ID.has(key) ? (key as OperatorGeneralWorkJobTypeId) : null;
}

/** Display label: Other → free text (or "Other"); known id → label; unknown slug → words. */
export function operatorGeneralWorkJobTypeLabel(jobType: unknown, otherLabel?: unknown): string {
  const raw = String(jobType ?? '').trim();
  const other = String(otherLabel ?? '').trim().slice(0, 80);
  if (!raw) return other || 'Service request';
  const id = normalizeOperatorGeneralWorkJobType(raw);
  if (id === 'other') return other || 'Other';
  if (id) return JOB_TYPE_BY_ID.get(id)!.label;
  const words = raw.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Approximate circle so globe geofence extent matches radiusMeters. */
export function operatorDeskRadiusWorkArea(
  lat: number,
  lng: number,
  radiusMeters: number,
): Array<{ lat: number; lng: number }> {
  const r = Math.max(25, Number(radiusMeters) || 150);
  const pts: Array<{ lat: number; lng: number }> = [];
  const cosLat = Math.max(0.2, Math.abs(Math.cos((lat * Math.PI) / 180)));
  for (let i = 0; i < 16; i += 1) {
    const a = (i / 16) * Math.PI * 2;
    pts.push({
      lat: lat + (r / 111_320) * Math.cos(a),
      lng: lng + (r / (111_320 * cosLat)) * Math.sin(a),
    });
  }
  return pts;
}

export function mapSentStatusToDeskStatus(raw: string): OperatorDeskWorkStatus {
  const s = String(raw || '').toLowerCase();
  if (s === 'completed' || s === 'accepted' || s === 'complete') return 'complete';
  if (s === 'assigned' || s === 'in_progress' || s === 'active' || s === 'progress') return 'progress';
  if (s === 'declined' || s === 'cancelled' || s === 'canceled') return 'cancelled';
  if (s === 'booked') return 'booked';
  return 'posted'; // New / open
}

/** Merge Operator sent-map pins into globe/map work items (status + geofence radius). */
export function sentMapPinToWorkItem(pin: {
  requestKey: string;
  service: string;
  status: string;
  statusLabel: string;
  contractorName?: string | null;
  siteName?: string | null;
  apiNumber?: string | null;
  latitude: number;
  longitude: number;
  radiusMeters: number;
  createdAt?: string | null;
}): OperatorDeskWorkItem {
  const status = mapSentStatusToDeskStatus(pin.status);
  return {
    id: pin.requestKey,
    source: 'guest',
    sourceId: pin.requestKey,
    listingKind: 'request',
    service: pin.service || 'Service request',
    serviceKind: /general/i.test(pin.service || '') ? 'other' : undefined,
    title: pin.siteName || pin.service || 'Sent request',
    status,
    statusLabel: pin.statusLabel || OPERATOR_DESK_STATUS_LABELS[status],
    statusColor: OPERATOR_DESK_STATUS_COLORS[status],
    apiNumber: pin.apiNumber || null,
    publicNumber: null,
    siteType: 'oilfield',
    facilityName: pin.siteName || null,
    operator: null,
    contractor: pin.contractorName || null,
    contractorId: null,
    lat: pin.latitude,
    lng: pin.longitude,
    createdAt: pin.createdAt || null,
    updates: [],
    questions: [],
    canUpdate: false,
    canAnswer: false,
    canPingLocation: false,
    workArea: operatorDeskRadiusWorkArea(pin.latitude, pin.longitude, pin.radiusMeters),
  };
}
